import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import QrScanner from "qr-scanner";

import {
  getKeylessSessionRAReview,
  validateKeylessVehicleQRCode,
} from "../keylessService";

import { DomainContext } from "../../../domain-repository/DomainRepository";
import { isTestsEnv, isValidPlateNumber } from "../../../utils/utils";
import { bigModalStyle, errorModalImgStyle, keylessColors } from "../utils";

import AlertModalManager from "../../../components/alert-modal-wraper/alert-modal-manager";
import Loading from "../../../components/Loading";
import { KeylessPageLayoutView } from "../KeylessPage";
import {
  KEYLESS_STEPS,
  KeylessPageLayout,
} from "../components/KeylessPageLayout";
import { KeylessButton } from "../components/KeylessButton";
import { KeylessModal } from "../components/KeylessModal";
import { QRCodeReader } from "../../../components/qrcode-reader/QRCodeReader";
import { CONVENTION_STEPS } from "./ConventionPage";

const vehicleSteps = {
  REVIEW: "REVIEW",
  PARKING_LOCATION: "PARKING_LOCATION",
  QR_CODE_EXPLAIN: "QR_CODE_EXPLAIN",
};

const isTests = isTestsEnv();

export function ConventionVehicleReview({ handleNextStep, vehicleDetails }) {
  const { t } = useTranslation();
  const hasRun = useRef(false);

  const { localizationAssets } = useContext(DomainContext);

  const [steps, setSteps] = useState([vehicleSteps.REVIEW]);
  const [currentStep, setCurrentStep] = useState(0);
  const [modalObj, setModalObj] = useState({
    isOpen: false,
  });

  if (!vehicleDetails) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        localizationAssets={localizationAssets}
        reservationNumber={""}
        footer={null}
      >
        <div style={styles.pageContent} className="container">
          <h1 style={styles.title}>Veículo de testes em falta</h1>
        </div>
      </KeylessPageLayout>
    );
  }

  if (steps[currentStep] === vehicleSteps.REVIEW) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        localizationAssets={localizationAssets}
        reservationNumber={""}
        footer={
          <KeylessButton
            onClick={() => handleNextStep(CONVENTION_STEPS.OPEN_CLOSE_DOORS)}
            text={t("keylessNext")}
            color="white"
            backgroundColor={keylessColors.orange}
          />
        }
      >
        <div style={styles.pageContent} className="container">
          <h1 style={styles.title}>{t("keylessVehicleTitle")}</h1>
          <img
            style={styles.vehicleImage}
            src={vehicleDetails?.vehicleImage}
            alt=""
          />
          <div style={styles.plateNumber}>
            <span>{t("keylessPlateNumber")}</span>
            <span>{vehicleDetails?.plateNumber}</span>
          </div>
          <p style={styles.brand}>{vehicleDetails?.groupDetails?.type}</p>
          <div style={styles.details}>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessFuelIcon"]}
                  alt=""
                />
              </div>
              <div>{t(vehicleDetails?.groupDetails?.fuel)}</div>
            </div>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessTransmissionIcon"]}
                  alt=""
                />
              </div>
              <div>{t(vehicleDetails?.groupDetails?.transmission)}</div>
            </div>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessColourIcon"]}
                  alt=""
                />
              </div>
              <div>
                {vehicleDetails?.colour === "PADRAO"
                  ? null
                  : t(
                      `vehicleColour-${vehicleDetails?.colour?.replace(
                        /\s+/g,
                        ""
                      )}`
                    )}
              </div>
            </div>
          </div>
        </div>
      </KeylessPageLayout>
    );
  }

  return <Loading active={true} />;
}

const styles = {
  page: {
    height: "100dvh",
    paddingBottom: "60px",
    fontFamily: "'BlissLight'",
  },
  pageContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    gap: "1rem",
  },
  filler: {
    flex: 1,
  },
  hint: {
    display: "flex",
    alignItems: "flex-start",
    gap: ".5rem",
  },
  hintIcon: {
    width: "16px",
  },
  title: {
    color: keylessColors.green,
    textAlign: "center",
    fontSize: "26px",
  },
  vehicleImage: {
    height: "13vh",
    margin: "0 auto",
    display: "block",
  },
  parkingImage: {
    height: "20vh",
    margin: "0 auto",
    display: "block",
  },
  plateNumber: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    color: "white",
    backgroundColor: keylessColors.green,
    borderRadius: "5px",
    padding: "4px 16px",
    margin: "0 auto",
    width: "max-content",
    fontSize: "20px",
  },
  brand: {
    textAlign: "center",
    fontSize: "16px",
    marginTop: "1rem",
  },
  details: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
  },
  detailsItem: {
    fontSize: "14px",
    display: "grid",
    justifyItems: "center",
  },
  detailsItemImgWrapper: {
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  detailsItemImg: {
    height: "60%",
  },
  parkingLocation: {
    backgroundColor: keylessColors.green,
    borderRadius: "20px",
    padding: "5px",
    width: "70%",
    color: "white",
    fontSize: "20px",
    margin: "0 auto",
    textAlign: "center",
  },
  parkingLocationValue: {
    fontSize: "20px",
  },
  qrcodeInputImg: {
    height: "20vh",
    margin: "0 auto",
    display: "block",
  },
  qrcodeInputWrapper: {
    position: "relative",
    width: "100%",
  },
  qrcodeInput: {
    opacity: "0",
    position: "absolute",
    width: "100%",
    height: "100%",
    top: "0",
    left: "0",
  },
};
